import {call, put, select} from 'redux-saga/effects';
import {updateCurrentSlide, updatePresentationLoadingSlides} from '../../actions/presentation.actions';
import {IRunFile} from '../../../incase-domain/proptypes/runfile';
import {createPresentationRunFile, fetchPresentationRunFile, fetchPresentationSlides} from '../../../service/presentation.service';
import {applyRulesToPresentation} from '../../../service/slide.service';
import {
    updateFullPresentationAction,
    updatePresentationAction,
    updatePresentationHasSlidesAction,
    updatePresLoadingStateAction,
} from '../../actions/dashboard.actions';
import {IPresentation} from '../../../incase-domain/presentation';
import {ISlide} from '../../../incase-domain/slide';
import {store} from '../../store';
import {fetchRoomPresentationSlides} from '../../../service/room.service';
import {updatePresentationRunFileAction} from '../../actions/runfile.actions';
import {createSlidesAnalyticsAction, fetchSlidesAnalyticsAction} from '../../actions/analytics.actions';
import {areObjectsEqual} from '../../../util/util';
import {getRunFileStorageKey, getSlugFromUrl} from '../../../util/token.utils';
import {AppState} from '../../reducer';
import {updateTimerAction} from '../../actions/timer.actions';

export async function getRunFile(
    presentation: IPresentation,
    requestParams?: Record<string, string>,
    isOwner?: boolean,
    roomOwnersRunFileId?: string,
    roomId?: string,
    roomRunfileId?: string,
    guestIdentification?: string
): Promise<IRunFile> {
    const slug = getSlugFromUrl();
    const runFileStorageKey = getRunFileStorageKey(presentation._id, roomRunfileId, slug);
    let runFileId = localStorage.getItem(runFileStorageKey);
    // Prevent creating a new runFile for the room owner; it is created on the server when starting room session
    if (isOwner && roomOwnersRunFileId && runFileId !== roomOwnersRunFileId) {
        runFileId = roomOwnersRunFileId;
        localStorage.setItem(runFileStorageKey, runFileId || '');
    }

    let runFile: IRunFile = {} as IRunFile;

    if (runFileId) {
        runFile = await fetchPresentationRunFile(runFileId);
    }

    const isPresRunfileAvailable = !!runFileId || !!runFile;
    const areParamsMatching = areObjectsEqual(requestParams, runFile?.requestParams);
    const isPresRunfileForCurrPres = runFile?.presentationId === presentation._id;

    if (!isPresRunfileAvailable || !isPresRunfileForCurrPres || !areParamsMatching) {
        runFile = await createPresentationRunFile(presentation._id, requestParams, roomId, roomRunfileId, guestIdentification);
        localStorage.setItem(runFileStorageKey, runFile?._id || '');

        if (presentation.dataCollection) store.dispatch(createSlidesAnalyticsAction(runFile._id || '', presentation._id));
    } else if (presentation.dataCollection && runFile._id) {
        store.dispatch(fetchSlidesAnalyticsAction(runFile._id));
    }

    store.dispatch(updatePresentationRunFileAction(runFile));
    store.dispatch(updateTimerAction(runFile.timeElapsed || 0));

    return runFile;
}

export function* loadSlidesSaga(
    presentation: IPresentation,
    password: string | null,
    roomId?: string,
    hash?: string,
    presenterCode?: string
): unknown {
    let page = 1;
    const pageSize = 128;
    let hasMoreSlides = true;
    let slides: ISlide[] = [];

    const updateLoadingState = (loadingState: number): void => {
        if (page === 1) store.dispatch(updatePresLoadingStateAction(loadingState));
    };

    const fetchService = roomId ? fetchRoomPresentationSlides : fetchPresentationSlides;

    do {
        const nextPresentationSlides: ISlide[] = yield call(
            fetchService,
            roomId || presentation._id,
            pageSize,
            page,
            password,
            updateLoadingState,
            hash,
            presenterCode
        );

        if (nextPresentationSlides && nextPresentationSlides.length > 0) {
            yield put(updatePresentationHasSlidesAction(true));

            slides = [...slides, ...nextPresentationSlides];

            const updatedPresentation = {
                ...presentation,
                slides,
            };

            yield call(refreshPresentationSaga, updatedPresentation);

            page += 1;
        } else {
            hasMoreSlides = false;
        }
    } while (hasMoreSlides);

    yield put(updatePresentationLoadingSlides(false));
    yield put(updatePresLoadingStateAction(0));
}

export function* refreshPresentationSaga(presentation: IPresentation): unknown {
    const state: AppState = yield select();
    const {room} = state.roomRunFileReducer;
    const {runFile} = state.runFileReducer;

    // needed to load presentation with all slides to reducer
    yield put(updateFullPresentationAction(presentation));
    const presentationWithRules = applyRulesToPresentation(presentation, runFile);
    yield put(updatePresentationAction(presentationWithRules));

    const lastSlideWatchedIndex = presentationWithRules.slides.map((s) => s._id).indexOf(runFile.lastSlideId || '');

    if (lastSlideWatchedIndex >= 0 && !room.roomId) {
        yield put(updateCurrentSlide(lastSlideWatchedIndex));
    }
}
